import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import cx from "classnames";
import { Button, Image, Modal } from "react-bootstrap";
import _ from "lodash";

import { createCampaign, fetchAudienceSegment, fetchCampaigns, invalidateCampaignSegments } from "actions/personalization";


import StoryboardContext from "components/Storyboard/context";

import styles from "./personalizationCampaign.module.scss";
import { LoadingMessage } from "../leadReports/components";
import { CampaignList } from "./components/CampaignsList";
import { AudienceSegementView } from "./components/AudienceSegment";
import { VariablesListView } from "./components/VariableList";

export default function PersonalizationCampaign() {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    updatedSegment: {
      name: "",
      commands: {},
      variables: [],
    },
    segmentOrder: null,
  })

  const storyboardContext = React.useContext(StoryboardContext);
  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', null));
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", []));
  const storyboardId = _.get(storyboard, "id", _.get(storyboardContext, "id"));
  const pageId = _.get(storyboard, "page.id", null);
  const campaigns = useSelector((state) => _.get(state, "personalization.campaigns.items", [])) || [];

  const selectedCampaign = useSelector((state) => {
    const selectedId = _.get(state, "personalization.campaigns.selectedCampaign", null);
    const campaigns = _.get(state, "personalization.campaigns.items", []);
    
    return campaigns.find((campaign) => campaign.id === selectedId) || null;
  });


  const segmentItems = useSelector((data) =>
    _.get(data, `personalization.segments.data.${selectedCampaign?.id}.items`, [])
  ) || [];

  const selectedSegment = useSelector((state) => {
    const selectedId = _.get(state, "personalization.segments.selectedSegment", null);
    
    return segmentItems.find((segment) => segment.id === selectedId) || null;
  });

  const loading = useSelector((state) => _.get(state, "personalization.campaigns.loading")) || false;


  useEffect(() => {
    if (!storyboardId) {
      return;
    }

    handleFetchCampaigns(0);

    return () => {
      dispatch(invalidateCampaignSegments())
    };
  }, []);

  const handleFetchCampaigns = (page) => {
    if (!storyboardId) {
      return;
    }

    const request = {
      storyboard: storyboardId,
      sort: "modified_date",
      order: "DESC",
    };

    dispatch(fetchCampaigns(request, page, 10));
  };


  useEffect(()=>{
    setState((prevState)=>{
      return {
        ...prevState,
        updatedSegment: {
          name: "",
          commands:  {},
          variables: [],
        }
      }
    })
  },[selectedSegment?.id]);

  const handleResetValue =() => {

    setState((prevState)=>{
      return {
        ...prevState,
        updatedSegment: {
          name: "",
          commands:  {},
          variables: [],
        },
        segmentOrder: null,
      }
    })
  }

  const handleUpdateValue = (type, value) => {
    setState((prevState) => {

      if(type == 'segmentOrder') {
        return {
          ...prevState,
          segmentOrder: value,
        };
      }

      return {
        ...prevState,
        updatedSegment: {
          ...prevState.updatedSegment,
          [type]: value,
        }
      };
    });
  }

  const handleCampaignPreview = () => {
    if (!pageId ||  !workspace || !selectedSegment){
      return;
    }
    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/pages/${pageId}/preview?s=${selectedSegment?.id}`;
    window.open(url);
  }

  return (
    <div className={styles.personalizationContent}>
      <div className={styles.contentWrapper}>
        <header className={styles.contentHeader}>
          <span className={styles.header}>Campaign</span>
          <span className={styles.description}>
            Build campaigns, define segments, and personalize content, all in one place.
          </span>
        </header>
        <Button
          variant="primary"
          className={styles.previewButton}
          onClick={handleCampaignPreview}
          disabled={segmentItems && segmentItems.length === 0}
        >
          <i className={cx("cleverstory-view")} />
          Preview by Segment
        </Button>
      </div>

      {loading && campaigns && campaigns.length === 0 && (
        <div className={cx(styles.loader, styles.modalLoader)}>
          <LoadingMessage className={styles.loadingMessage} />
        </div>
      )}
      {!loading && campaigns && campaigns.length === 0 && (
        <div className={styles.personalizationRow}>
          <PersonalizationEmptyMessage storyboard={storyboardId} />
        </div>
      )}
      { campaigns && campaigns.length > 0 && (
        <div className={styles.personalizationRow}>
            <CampaignList/>
          <div className={styles.segmentsColumn}>
            <AudienceSegementView
              data={state}
              storyboard={storyboardId}
              handleUpdateValue={handleUpdateValue}
            />
          </div>
          <div className={styles.variablesColumn}>
            <VariablesListView
              data={state}
              storyboard={storyboardId}
              handleResetValue={handleResetValue}
              handleUpdateValue={handleUpdateValue}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export const PersonalizationEmptyMessage = ({ storyboard }) => {
  const dispatch = useDispatch();

  const creating = useSelector((state) =>
    _.get(state, `personalization.campaigns.creating`, false)
  );

  const handleCreateCampaign = () => {
    const request = {
      name: "New Campaign",
      enabled: false,
      storyboard: storyboard,
    };

    dispatch(createCampaign(request));
  };

  return (
    <div className={styles.emptyMediaContainer}>
      <div className={styles.emptyMessage}>
        <img
          className={cx(styles.emptyMediaImage)}
          src=""
          alt="emptyMedia_image"
        />
        <div className={styles.errorMessageContent}>
          <Button
            disabled={creating}
            variant="primary"
            className={styles.createButton}
            onClick={handleCreateCampaign}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
            Create Campaign
          </Button>
          <div className={styles.errorMessageTitle}>
            <h2 className={styles.heading}>Weather forecast</h2>
          </div>
          <div className={styles.errorMessageSubText}>
            <span>
              Clear skies and <br /> empty screens
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};



export const DeletePersonalizationModal = ({ deleteModal, deleting, showDeleteModal, onDelete }) => {

  const handleDeleteClick = (e) => {
    onDelete();
    e.stopPropagation();
  };

  return (
    <Modal
      className={styles.deletePageModal}
      contentClassName={styles.deletePageModalContent}
      dialogClassName={styles.deletePageModalDialog}
      backdropClassName={styles.backdropWrapper}
      show={deleteModal}
      centered
      size="lg"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Modal.Body className={styles.deletePageModalBody}>
        <div className={styles.detailsContainer}>
          <div className={styles.messageContainer}>
            <h2 className={styles.messageTitle}>Are you sure?</h2>
            <p className={styles.messageContent}>
              Are you sure you want to delete ? This action is permanent and cannot be undone.
            </p>
            <footer className={styles.deletePageModalButtons}>
              <Button
                variant="secondary"
                onClick={() => {
                  showDeleteModal(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDeleteClick}>
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </footer>
          </div>
          <div>
            <Image
              src={"/images/deletePage.png"}
              className={styles.deletePageModalImage}
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
